import { useQuery } from '../models/reactUtils'
import React, { useId, useState } from 'react'

import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'

import { PRACTICE_LOGIN_URL } from '../constants'
import { SUBMIT_PROVIDER_CORRECTION } from '../models/queries'
import DetailIcon from './DetailIcon'

import './ProviderDetailsPanel.css'

export default function ProviderCorrectionDialog({
  providerId,
  providerName,
  onClose,
}) {
  const { store } = useQuery()
  const [step, setStep] = useState('choice')
  const [values, setValues] = useState({ name: '', email: '', comment: '' })
  const [error, setError] = useState('')
  const [sending, setSending] = useState(false)
  const titleId = useId()
  const descriptionId = useId()
  const fieldId = useId()

  const submit = async event => {
    event.preventDefault()
    if (sending) return
    if (!values.name.trim() || !values.email.trim() || !values.comment.trim()) {
      setError('Please enter your name, email, and comment.')
      return
    }
    setSending(true)
    setError('')
    try {
      const result = await store.mutate(SUBMIT_PROVIDER_CORRECTION, {
        providerId,
        partnerId: store.partnerId || null,
        name: values.name.trim(),
        email: values.email.trim(),
        comment: values.comment.trim(),
      })
      if (result?.submitProviderCorrection !== true)
        throw new Error('Submission failed')
      setStep('sent')
    } catch {
      setError('Your correction could not be sent. Please try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby={titleId}
      aria-describedby={step === 'choice' ? descriptionId : undefined}
      BackdropProps={{ sx: { bgcolor: 'rgba(44,42,41,.4)' } }}
      PaperProps={{
        className: 'provider-details correction-dialog',
        sx: {
          width: 408,
          maxWidth: 'calc(100% - 48px)',
          m: 3,
          p: '24px 27px',
          maxHeight: 'calc(100dvh - 48px)',
          borderRadius: '16px',
          boxShadow: '0 4px 4px rgba(0,0,0,.25)',
        },
      }}
    >
      <IconButton
        aria-label="Close correction form"
        title="Close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          width: 36,
          height: 36,
          p: 0,
          bgcolor: '#fff',
          boxShadow: '0 1px 3px rgba(44,42,41,.12)',
        }}
      >
        <DetailIcon name="close" />
      </IconButton>
      <h2 id={titleId} className="correction-title">
        {step === 'choice'
          ? 'Report an update'
          : step === 'sent'
            ? 'Thanks for the correction'
            : 'What needs correcting?'}
      </h2>
      {step === 'choice' ? (
        <>
          <p id={descriptionId}>
            Tell us who you are so we can route the correction for{' '}
            {providerName}.
          </p>
          <button
            className="correction-role"
            type="button"
            onClick={() => {
              setStep('form')
              setError('')
            }}
          >
            <strong>I'm a patient</strong>
            <span>Send a note about what looks wrong</span>
          </button>
          <a
            className="correction-role"
            href={PRACTICE_LOGIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
          >
            <strong>I'm the provider</strong>
            <span>Edit this listing in the practice dashboard</span>
          </a>
        </>
      ) : step === 'sent' ? (
        <>
          <p>
            Intelibly support will review your correction for {providerName}.
          </p>
          <button type="button" className="correction-submit" onClick={onClose}>
            Done
          </button>
        </>
      ) : (
        <form className="correction-form" onSubmit={submit}>
          {['name', 'email', 'comment'].map(field => (
            <div className="correction-field" key={field}>
              <label htmlFor={fieldId + '-' + field}>
                {field === 'name'
                  ? 'Name'
                  : field === 'email'
                    ? 'Email'
                    : 'Comment'}
              </label>
              {field === 'comment' ? (
                <textarea
                  id={fieldId + '-' + field}
                  name={field}
                  required
                  disabled={sending}
                  maxLength={5000}
                  value={values[field]}
                  onChange={event =>
                    setValues({ ...values, [field]: event.target.value })
                  }
                />
              ) : (
                <input
                  id={fieldId + '-' + field}
                  name={field}
                  type={field === 'email' ? 'email' : 'text'}
                  autoComplete={field}
                  required
                  disabled={sending}
                  maxLength={field === 'email' ? 254 : 200}
                  value={values[field]}
                  onChange={event =>
                    setValues({ ...values, [field]: event.target.value })
                  }
                />
              )}
            </div>
          ))}
          {error && (
            <p role="alert" className="correction-error">
              {error}
            </p>
          )}
          <button
            className="correction-submit"
            type="submit"
            disabled={sending}
          >
            {sending ? 'Sending...' : 'Submit correction'}
          </button>
          <button
            className="correction-back"
            type="button"
            onClick={() => setStep('choice')}
          >
            Back
          </button>
        </form>
      )}
    </Dialog>
  )
}
